import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AngelIcon from '@/components/base/AngelIcon';
import { supabase } from '@/lib/supabase';
import { useAuthState } from '@/components/feature/AuthGuard';

interface ProfileForm {
  nickname: string;
  lastName: string;
  firstName: string;
  email: string;
  country: string;
  idNumber: string;
  idPhoto: string;
  selfie: string;
  birthday: string;
  bloodType: string;
  highestEducation: string;
  schoolName1: string;
  secondEducation: string;
  schoolName2: string;
  professionalExp: string;
  interests: string;
}

const initialForm: ProfileForm = {
  nickname: '',
  lastName: '',
  firstName: '',
  email: '',
  country: '',
  idNumber: '',
  idPhoto: '',
  selfie: '',
  birthday: '',
  bloodType: '',
  highestEducation: '',
  schoolName1: '',
  secondEducation: '',
  schoolName2: '',
  professionalExp: '',
  interests: '',
};

const countryOptions = ['Taiwan', 'United States', 'Japan', 'South Korea', 'Singapore', 'Malaysia', 'Hong Kong', 'China', 'Canada', 'Australia', 'United Kingdom', 'Germany', 'France', 'Other'];
const bloodTypeOptions = ['A', 'B', 'AB', 'O', 'Unknown'];

export default function LivingProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuthState();
  const [form, setForm] = useState<ProfileForm>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [idPhotoName, setIdPhotoName] = useState('');
  const [selfieName, setSelfieName] = useState('');
  const [idPhotoFile, setIdPhotoFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof ProfileForm, string>>>({});

  const updateField = (field: keyof ProfileForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleFileSelect = (field: 'idPhoto' | 'selfie', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (field === 'idPhoto') {
        setIdPhotoName(file.name);
        setIdPhotoFile(file);
      } else {
        setSelfieName(file.name);
        setSelfieFile(file);
      }
      updateField(field, file.name);
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ProfileForm, string>> = {};
    if (!form.nickname.trim()) newErrors.nickname = 'Required';
    if (!form.lastName.trim()) newErrors.lastName = 'Required';
    if (!form.firstName.trim()) newErrors.firstName = 'Required';
    if (!form.email.trim()) newErrors.email = 'Required';
    if (!form.country) newErrors.country = 'Required';
    if (!form.idNumber.trim()) newErrors.idNumber = 'Required';
    if (!form.idPhoto) newErrors.idPhoto = 'Required';
    if (!form.selfie) newErrors.selfie = 'Required';
    if (!form.birthday) newErrors.birthday = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sanitizeFileName = (raw: string): string => {
    const ext = raw.split('.').pop()?.toLowerCase() || 'bin';
    const base = raw
      .replace(/\.[^.]+$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9._-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[-.]+|[-.]+$/g, '')
      .slice(0, 40);
    return `${base || 'file'}.${ext}`;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!auth.user) {
      setSaveError('請先登入後再送出。');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const uid = auth.user.id;
      let idPhotoPath = '';
      let selfiePath = '';

      if (idPhotoFile) {
        const path = `living/${uid}/id-${Date.now()}-${sanitizeFileName(idPhotoFile.name)}`;
        const { error: upErr } = await supabase.storage.from('private').upload(path, idPhotoFile);
        if (upErr) throw upErr;
        idPhotoPath = path;
      }
      if (selfieFile) {
        const path = `living/${uid}/selfie-${Date.now()}-${sanitizeFileName(selfieFile.name)}`;
        const { error: upErr } = await supabase.storage.from('private').upload(path, selfieFile);
        if (upErr) throw upErr;
        selfiePath = path;
      }

      const { error: rpcErr } = await supabase.rpc('save_living_profile', {
        p: {
          nickname: form.nickname,
          last_name: form.lastName,
          first_name: form.firstName,
          email: form.email,
          country: form.country,
          id_number: form.idNumber,
          id_photo_path: idPhotoPath || undefined,
          selfie_path: selfiePath || undefined,
          birthday: form.birthday,
          blood_type: form.bloodType,
          highest_education: form.highestEducation,
          school_name_1: form.schoolName1,
          second_education: form.secondEducation,
          school_name_2: form.schoolName2,
          professional_exp: form.professionalExp,
          interests: form.interests,
        },
      });
      if (rpcErr) throw rpcErr;
      setSubmitted(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '保存失敗');
    } finally {
      setSaving(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-900">
        <Navbar />
        <main>
          <section className="max-w-2xl mx-auto px-4 md:px-6 pt-32 pb-20 text-center">
            <div className="bg-accent-50/30 border border-accent-200/50 rounded-lg p-10 md:p-14">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100/70 flex items-center justify-center">
                <i className="ri-check-line text-2xl text-accent-600" />
              </div>
              <h2
                className="text-2xl md:text-3xl mb-3 text-foreground-900"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                {t('livingProfile.submitted')}
              </h2>
              <p className="text-sm text-foreground-400 mb-1">{t('livingProfile.submittedEn')}</p>
              <p className="text-sm text-foreground-500 max-w-md mx-auto mb-8 leading-relaxed">
                {t('livingProfile.submittedDetail')}
              </p>
              <Link
                to="/qualification"
                className="whitespace-nowrap inline-flex px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer"
              >
                {t('livingProfile.continueToQualification')}
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main>
        {/* ── Header ──────────────────────────────────────── */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-8 text-center">
          <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 mb-4">
            {t('livingProfile.title')} &middot; {t('livingProfile.titleZh')}
          </p>
          <h1
            className="text-3xl md:text-4xl mb-3 text-foreground-900"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('livingProfile.title')}
          </h1>
          <p className="text-sm text-foreground-500 max-w-lg mx-auto mb-2">
            {t('livingProfile.subtitle')}
          </p>
          <p className="text-xs text-foreground-400 max-w-lg mx-auto mb-2">
            {t('livingProfile.subtitleEn')}
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-foreground-300">
            <AngelIcon variant="default" size="sm" />
            <span>{t('livingProfile.note')}</span>
          </div>
        </section>

        {/* ── Form ────────────────────────────────────────── */}
        <section className="max-w-2xl mx-auto px-4 md:px-6 pb-20">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Required fields section */}
            <div className="bg-background-100/40 border border-foreground-200/50 rounded-lg p-6 space-y-5">
              <p className="text-xs text-foreground-400 uppercase tracking-wider">
                {t('livingProfile.fields.required')} &mdash; {t('livingProfile.fields.requiredEn')}
              </p>

              {/* Nickname */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">
                  {t('livingProfile.fields.nickname')} <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.nickname}
                  onChange={(e) => updateField('nickname', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-md border text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 ${
                    errors.nickname ? 'border-red-300' : 'border-foreground-200'
                  }`}
                  placeholder={t('livingProfile.fields.nicknameEn')}
                />
                {errors.nickname && <p className="text-xs text-red-400 mt-1">{errors.nickname}</p>}
              </div>

              {/* Name row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-foreground-700 mb-1.5">
                    {t('livingProfile.fields.lastName')} <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.lastName}
                    onChange={(e) => updateField('lastName', e.target.value)}
                    className={`w-full px-3 py-2.5 rounded-md border text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 ${
                      errors.lastName ? 'border-red-300' : 'border-foreground-200'
                    }`}
                    placeholder={t('livingProfile.fields.lastNameEn')}
                  />
                  {errors.lastName && <p className="text-xs text-red-400 mt-1">{errors.lastName}</p>}
                </div>
                <div>
                  <label className="block text-sm text-foreground-700 mb-1.5">
                    {t('livingProfile.fields.firstName')} <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.firstName}
                    onChange={(e) => updateField('firstName', e.target.value)}
                    className={`w-full px-3 py-2.5 rounded-md border text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 ${
                      errors.firstName ? 'border-red-300' : 'border-foreground-200'
                    }`}
                    placeholder={t('livingProfile.fields.firstNameEn')}
                  />
                  {errors.firstName && <p className="text-xs text-red-400 mt-1">{errors.firstName}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">
                  {t('livingProfile.fields.email')} <span className="text-accent-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-md border text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 ${
                    errors.email ? 'border-red-300' : 'border-foreground-200'
                  }`}
                  placeholder="you@example.com"
                />
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">
                  {t('livingProfile.fields.country')} <span className="text-accent-500">*</span>
                </label>
                <select
                  value={form.country}
                  onChange={(e) => updateField('country', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-md border text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 ${
                    errors.country ? 'border-red-300' : 'border-foreground-200'
                  }`}
                >
                  <option value="">{t('livingProfile.fields.countryEn')}...</option>
                  {countryOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {errors.country && <p className="text-xs text-red-400 mt-1">{errors.country}</p>}
              </div>

              {/* ID Number */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">
                  {t('livingProfile.fields.idNumber')} <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.idNumber}
                  onChange={(e) => updateField('idNumber', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-md border text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 ${
                    errors.idNumber ? 'border-red-300' : 'border-foreground-200'
                  }`}
                  placeholder={t('livingProfile.fields.idNumberEn')}
                />
                {errors.idNumber && <p className="text-xs text-red-400 mt-1">{errors.idNumber}</p>}
              </div>

              {/* ID Photo */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">
                  {t('livingProfile.fields.idPhoto')} <span className="text-accent-500">*</span>
                </label>
                <div className={`border-2 border-dashed rounded-md p-4 text-center transition-colors ${
                  errors.idPhoto ? 'border-red-300' : 'border-foreground-200 hover:border-accent-300'
                }`}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileSelect('idPhoto', e)}
                    className="hidden"
                    id="id-photo-upload"
                  />
                  <label htmlFor="id-photo-upload" className="cursor-pointer text-sm text-foreground-500">
                    {idPhotoName ? (
                      <span className="text-accent-600">{idPhotoName}</span>
                    ) : (
                      <>
                        <i className="ri-camera-line text-xl block mb-1" />
                        {t('livingProfile.fields.idPhotoEn')}
                      </>
                    )}
                  </label>
                </div>
                {errors.idPhoto && <p className="text-xs text-red-400 mt-1">{errors.idPhoto}</p>}
              </div>

              {/* Selfie */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">
                  {t('livingProfile.fields.selfie')} <span className="text-accent-500">*</span>
                </label>
                <div className={`border-2 border-dashed rounded-md p-4 text-center transition-colors ${
                  errors.selfie ? 'border-red-300' : 'border-foreground-200 hover:border-accent-300'
                }`}>
                  <input
                    type="file"
                    accept="image/*"
                    capture="user"
                    onChange={(e) => handleFileSelect('selfie', e)}
                    className="hidden"
                    id="selfie-upload"
                  />
                  <label htmlFor="selfie-upload" className="cursor-pointer text-sm text-foreground-500">
                    {selfieName ? (
                      <span className="text-accent-600">{selfieName}</span>
                    ) : (
                      <>
                        <i className="ri-user-smile-line text-xl block mb-1" />
                        {t('livingProfile.fields.selfieEn')}
                      </>
                    )}
                  </label>
                </div>
                {errors.selfie && <p className="text-xs text-red-400 mt-1">{errors.selfie}</p>}
              </div>

              {/* Birthday */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">
                  {t('livingProfile.fields.birthday')} <span className="text-accent-500">*</span>
                </label>
                <input
                  type="date"
                  value={form.birthday}
                  onChange={(e) => updateField('birthday', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-md border text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400 ${
                    errors.birthday ? 'border-red-300' : 'border-foreground-200'
                  }`}
                />
                {errors.birthday && <p className="text-xs text-red-400 mt-1">{errors.birthday}</p>}
              </div>
            </div>

            {/* Optional fields section */}
            <div className="bg-background-100/40 border border-foreground-200/50 rounded-lg p-6 space-y-5">
              <p className="text-xs text-foreground-400 uppercase tracking-wider">Optional</p>

              {/* Blood Type */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">{t('livingProfile.fields.bloodType')}</label>
                <select
                  value={form.bloodType}
                  onChange={(e) => updateField('bloodType', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
                >
                  <option value="">{t('livingProfile.fields.bloodTypeEn')}...</option>
                  {bloodTypeOptions.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Highest Education + School */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-foreground-700 mb-1.5">{t('livingProfile.fields.highestEducation')}</label>
                  <input
                    type="text"
                    value={form.highestEducation}
                    onChange={(e) => updateField('highestEducation', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
                    placeholder={t('livingProfile.fields.highestEducationEn')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-foreground-700 mb-1.5">{t('livingProfile.fields.schoolName1')}</label>
                  <input
                    type="text"
                    value={form.schoolName1}
                    onChange={(e) => updateField('schoolName1', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
                    placeholder={t('livingProfile.fields.schoolName1En')}
                  />
                </div>
              </div>

              {/* Second Education + School */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-foreground-700 mb-1.5">{t('livingProfile.fields.secondEducation')}</label>
                  <input
                    type="text"
                    value={form.secondEducation}
                    onChange={(e) => updateField('secondEducation', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
                    placeholder={t('livingProfile.fields.secondEducationEn')}
                  />
                </div>
                <div>
                  <label className="block text-sm text-foreground-700 mb-1.5">{t('livingProfile.fields.schoolName2')}</label>
                  <input
                    type="text"
                    value={form.schoolName2}
                    onChange={(e) => updateField('schoolName2', e.target.value)}
                    className="w-full px-3 py-2.5 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
                    placeholder={t('livingProfile.fields.schoolName2En')}
                  />
                </div>
              </div>

              {/* Professional Experience */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">{t('livingProfile.fields.professionalExp')}</label>
                <input
                  type="text"
                  value={form.professionalExp}
                  onChange={(e) => updateField('professionalExp', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
                  placeholder={t('livingProfile.fields.professionalExpEn')}
                />
              </div>

              {/* Interests */}
              <div>
                <label className="block text-sm text-foreground-700 mb-1.5">{t('livingProfile.fields.interests')}</label>
                <input
                  type="text"
                  value={form.interests}
                  onChange={(e) => updateField('interests', e.target.value)}
                  className="w-full px-3 py-2.5 rounded-md border border-foreground-200 text-sm bg-background-50 focus:outline-none focus:ring-1 focus:ring-accent-400"
                  placeholder={t('livingProfile.fields.interestsEn')}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="text-center pt-4">
              {saveError && (
                <p className="text-xs text-rose-600 bg-rose-50 rounded-md px-4 py-2.5 mb-3 inline-block">
                  {saveError}
                </p>
              )}
              <button
                type="submit"
                disabled={saving}
                className="whitespace-nowrap px-10 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
              >
                {saving ? '保存中…' : t('livingProfile.submit')}
              </button>
            </div>
          </form>
        </section>
      </main>
      <Footer />
    </div>
  );
}